import { useState, useEffect } from 'react';
import { getCurrentYear, getYearRange, getAcademicYear, getFiscalYear } from '../utils/dateUtils';

/**
 * Custom hook for dynamic year functionality
 * @param {Object} options - Configuration options
 * @param {number} options.startYear - Start year for ranges
 * @param {number} options.endYear - End year for ranges
 * @param {string} options.type - Type of year ('current', 'range', 'academic', 'fiscal')
 * @returns {Object} Year-related data and functions
 */
export const useDynamicYear = (options = {}) => {
  const { startYear, endYear, type = 'current' } = options;
  const [currentYear, setCurrentYear] = useState(getCurrentYear());

  useEffect(() => {
    // Update year on mount and when year changes
    const updateYear = () => {
      setCurrentYear(getCurrentYear());
    };

    // Update immediately
    updateYear();

    // Set up interval to check for year changes (useful around New Year)
    const interval = setInterval(() => {
      const newYear = getCurrentYear();
      if (newYear !== currentYear) {
        updateYear();
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [currentYear]);

  const getYear = () => {
    switch (type) {
      case 'range':
        return getYearRange(startYear, endYear);
      case 'academic':
        return getAcademicYear(currentYear);
      case 'fiscal':
        return getFiscalYear(currentYear);
      case 'current':
      default:
        return currentYear;
    }
  };

  const getYearString = () => {
    return getYear().toString();
  };

  const getYearRangeString = (start, end) => {
    return getYearRange(start, end);
  };

  const getAcademicYearString = (year) => {
    return getAcademicYear(year);
  };

  const getFiscalYearString = (year) => {
    return getFiscalYear(year);
  };

  return {
    currentYear,
    year: getYear(),
    yearString: getYearString(),
    getYearRangeString,
    getAcademicYearString,
    getFiscalYearString,
    // Utility functions
    isLeapYear: () => (currentYear % 4 === 0 && currentYear % 100 !== 0) || (currentYear % 400 === 0),
    getDecade: () => Math.floor(currentYear / 10) * 10,
    getCentury: () => Math.floor(currentYear / 100) * 100,
  };
};

export default useDynamicYear;
